import { API_BASE_URL } from "../apiConfig";
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./RecyclingDashboard.css";

const itemIcons = {
  Plastic: "PL",
  Cardboard: "CB",
  Paper: "PA",
  "Metal Can": "MC",
  Glass: "GL",
};

const getIcon = (name) => itemIcons[name] || "♻️";

const formatDate = (d) =>
  new Date(d).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const getNotificationLabel = (notification) => {
  if (notification.status === "collected") return "Collected by your company";
  if (notification.status === "cancelled_by_others") {
    return `Collected by ${notification.collectedByCompanyName || "another company"}`;
  }
  return "Pending";
};

const RecyclingDashboard = () => {
  const navigate = useNavigate();

  const [companyId, setCompanyId] = useState(null);
  const [companyName, setCompanyName] = useState("Recycling Co");
  const [stats, setStats] = useState({ pending: 0, collected: 0, total: 0, detected: 0 });
  const [notifications, setNotifications] = useState([]);
  const [detectedItems, setDetectedItems] = useState([]);
  const [collecting, setCollecting] = useState({});
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem("currentUser");
      if (!stored) return;

      const parsed = JSON.parse(stored);
      if (parsed.role === "recycling_company") {
        setCompanyName(parsed.companyName || parsed.username);
        setCompanyId(parsed._id || null);
      }
    } catch (e) {
      console.error("Failed to read recycling company session:", e);
    }
  }, []);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchDashboardData = useCallback(async () => {
    if (!companyId) return;

    try {
      setLoading(true);
      const [notifRes, detectedRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/recycling/notifications/${companyId}`),
        fetch(`${API_BASE_URL}/api/recycling/all-detected`),
      ]);
      const notifData = await notifRes.json();
      const detectedData = await detectedRes.json();

      const notifList = Array.isArray(notifData) ? notifData : [];
      const detectedList = Array.isArray(detectedData) ? detectedData : [];

      setNotifications(notifList);
      setDetectedItems(detectedList);

      setStats({
        pending: notifList.filter((n) => n.status === "pending").length,
        collected: notifList.filter((n) => n.status === "collected").length,
        total: notifList.length,
        detected: detectedList.length,
      });
    } catch (err) {
      console.error("Error fetching recycling dashboard:", err);
      showToast("Failed to load recycling dashboard", "error");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (companyId) fetchDashboardData();
    else setLoading(false);
  }, [companyId, fetchDashboardData]);

  const handleCollect = async (notifId) => {
    setCollecting((prev) => ({ ...prev, [notifId]: true }));
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/recycling/notifications/collect/${notifId}`,
        { method: "PUT" }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to update collection");

      showToast("Items marked as collected.", "success");
      fetchDashboardData();
    } catch (err) {
      showToast(err.message || "Failed to update", "error");
    } finally {
      setCollecting((prev) => ({ ...prev, [notifId]: false }));
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleLogout = () => {
    sessionStorage.removeItem("currentUser");
    navigate("/");
  };

  return (
    <div className="rcd-page">
      {toast && <div className={`rcd-toast rcd-toast-${toast.type}`}>{toast.msg}</div>}

      <div className="rcd-topbar">
        <div>
          <h1>Recycling Dashboard</h1>
          <p>
            Welcome back, <strong>{companyName}</strong>
          </p>
        </div>
      </div>

      {!companyId ? (
        <div className="rcd-no-id">Company ID not found. Please log out and log in again.</div>
      ) : loading ? (
        <div className="rcd-loading">Loading dashboard data...</div>
      ) : (
        <>
          <div className="rcd-stats-row">
            <div className="rcd-stat-card blue">
              <div className="rcd-stat-icon">TN</div>
              <div className="rcd-stat-info">
                <span className="rcd-stat-value">{stats.total}</span>
                <span className="rcd-stat-label">Total Notifications</span>
              </div>
            </div>
            <div className="rcd-stat-card orange">
              <div className="rcd-stat-icon">PP</div>
              <div className="rcd-stat-info">
                <span className="rcd-stat-value">{stats.pending}</span>
                <span className="rcd-stat-label">Pending Pickups</span>
              </div>
            </div>
            <div className="rcd-stat-card green">
              <div className="rcd-stat-icon">CC</div>
              <div className="rcd-stat-info">
                <span className="rcd-stat-value">{stats.collected}</span>
                <span className="rcd-stat-label">Completed Collections</span>
              </div>
            </div>
            <div className="rcd-stat-card teal">
              <div className="rcd-stat-icon">ID</div>
              <div className="rcd-stat-info">
                <span className="rcd-stat-value">{stats.detected}</span>
                <span className="rcd-stat-label">Items Detected System-wide</span>
              </div>
            </div>
          </div>

          <div className="rcd-body">
            {/* Left Panel: Incoming Notifications */}
            <div className="rcd-panel">
              <div className="rcd-panel-header orange-header">
                <span>IN</span>
                <h2>Incoming Notifications</h2>
                <span className="rcd-badge orange-badge">{stats.pending}</span>
              </div>

              {notifications.length === 0 ? (
                <div className="rcd-empty">
                  <span>--</span>
                  <p>No notifications received yet.</p>
                </div>
              ) : (
                <div className="rcd-notif-list">
                  {notifications.map((notification) => (
                    <div
                      key={notification._id}
                      className={`rcd-notif-card ${
                        notification.status
                      }`}
                    >
                      <div className="rcd-notif-top">
                        <span className="rcd-notif-icon">{getIcon(notification.itemType)}</span>
                        <div className="rcd-notif-details">
                          <span className="rcd-notif-type">{notification.itemType}</span>
                          <span className="rcd-notif-qty">Quantity: {notification.quantity}</span>
                          <span className="rcd-notif-loc">
                            Location: {notification.location?.lat?.toFixed(4)},{" "}
                            {notification.location?.lng?.toFixed(4)}
                          </span>
                          <span className="rcd-notif-date">{formatDate(notification.createdAt)}</span>
                        </div>
                        <span className={`rcd-notif-pill ${notification.status}`}>
                          {getNotificationLabel(notification)}
                        </span>
                      </div>
                      {notification.status === "pending" && (
                        <button
                          className="rcd-collect-btn"
                          onClick={() => handleCollect(notification._id)}
                          disabled={collecting[notification._id]}
                        >
                          {collecting[notification._id] ? "Updating..." : "Mark as Collected"}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right Panel: Items Detected */}
            <div className="rcd-panel">
              <div className="rcd-panel-header green-header">
                <span>ID</span>
                <h2>Items Detected System-wide</h2>
                <span className="rcd-badge green-badge">{detectedItems.length}</span>
              </div>

              {detectedItems.length === 0 ? (
                <div className="rcd-empty">
                  <span>--</span>
                  <p>No recyclable items detected.</p>
                </div>
              ) : (
                <div className="rcd-detected-list">
                  {detectedItems.map((item, index) => (
                    <div key={`${item._id}-${index}`} className="rcd-detected-card">
                      <div className="rcd-detected-icon">{getIcon(item.className)}</div>
                      <div className="rcd-detected-info">
                        <span className="rcd-detected-name">{item.className}</span>
                        <span className="rcd-detected-conf">
                          Confidence: {Math.round(Number(item.confidence || 0) * 100)}%
                        </span>
                        {item.description && (
                          <span className="rcd-detected-desc" title={item.description}>
                            "{item.description.substring(0, 50)}..."
                          </span>
                        )}
                        <span className="rcd-detected-date">{formatDate(item.createdAt)}</span>
                      </div>
                      <div className="rcd-detected-loc">
                        {item.location?.lat?.toFixed(3)}, {item.location?.lng?.toFixed(3)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RecyclingDashboard;
